<template>
  <BaseLink
    :to="!isButton ? url : undefined"
    :tag="isButton ? 'button' : undefined"
    class="ui-button-link-round"
    :class="[
      `page-${serviceTypeValue}`,
      styled && `styled-${styled}`,
      size && `size-${size}`,
    ]"
    :icon-size="iconSize"
    :show-icon="!icon"
    @click="emits('click')"
  >
    <slot />
    <BaseIcon
      v-if="icon"
      :name="icon"
      :size="iconSize"
      class="icon"
    />
  </BaseLink>
</template>

<script lang="ts" setup>
import type { SVG_NAMES } from '~/components/icons'

interface Props {
  url?: string
  styled?: 'primary' | 'secondary' | 'tertiary'
  isExternal?: boolean
  serviceType?: 'hina' | 'jps'
  icon?: SVG_NAMES
  size?: 'large' | 'small'
}

interface Emits {
  (e: 'click'): void
}

const props = withDefaults(defineProps<Props>(), {
  size: 'small'
})
const emits = defineEmits<Emits>()

const iconSize = 'size-6';
const isButton = props.url === undefined

const { serviceType } = useServiceType()
const serviceTypeValue = props.serviceType || serviceType
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.page-jps {
  --primary-background-color: #{$jps-sub-blue};
  --primary-text-color: #{$white};
  --primary-border-color: #{$jps-sub-blue-active};
  --primary-hover-text-color: #{$white};
  --primary-hover-border-color: #{$jps-sub-blue};
  --primary-hover-background-color: #{$jps-sub-blue-hover};

  --secondary-background-color: #005ecc;
  --secondary-text-color: #{$white};
  --secondary-border-color: #005ecc;
  --secondary-hover-text-color: #{$white};
  --secondary-hover-border-color: #005ecc;
  --secondary-hover-background-color: #005ecc;

  --tertiary-background-color: #{$jps-gray-900};
  --tertiary-text-color: #{$white};
  --tertiary-border-color: #{$jps-gray-900};
  --tertiary-hover-text-color: #{$white};
  --tertiary-hover-border-color: #{$jps-gray-800};
  --tertiary-hover-background-color: #{$jps-gray-800};
}

.page-hina {
  --primary-background-color: #{$hina-background-green};
  --primary-text-color: #005ecc;
  --primary-border-color: #{$hina-accent-green-accessible};
  --primary-hover-text-color: #{$white};
  --primary-hover-border-color: #{$hina-accent-green-accessible};
  --primary-hover-background-color: #{$hina-accent-green-accessible};

  --secondary-background-color: #005ecc;
  --secondary-text-color: #{$white};
  --secondary-border-color: #005ecc;
  --secondary-hover-text-color: #{$white};
  --secondary-hover-border-color: #005ecc;
  --secondary-hover-background-color: #005ecc;

  --tertiary-background-color: #{$white};
  --tertiary-text-color: #005ecc;
  --tertiary-border-color: #005ecc;
  --tertiary-hover-text-color: #{$white};
  --tertiary-hover-border-color: #005ecc;
  --tertiary-hover-background-color: #005ecc;
}

.ui-button-link-round {
  @include no-user-select;
  @include no-decorative-button;
  @include transition-all;

  font-weight: 500;
  border-radius: 9999px;
  cursor: pointer;
  line-height: 1.4;

  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;

  &.size-large {
    padding: 0.5rem 1rem;
    font-size: $size-6;
  }

  &.size-small {
    padding: 0.25rem 1rem;
    font-size: $size-7;
  }

  &.styled-primary,
  &.styled-secondary,
  &.styled-tertiary {
    border-width: 1px;
    border-style: solid;
  }

  &.styled-primary {
    &,
    &:visited {
      background: var(--primary-background-color);
      color: var(--primary-text-color);
      border-color: var(--primary-border-color);
    }

    &:not(:disabled):hover,
    &:not(:disabled):focus {
      color: var(--primary-hover-text-color);
      border-color: var(--primary-hover-border-color);
      background-color: var(--primary-hover-background-color);
    }
  }

  &.styled-secondary {
    &,
    &:visited {
      background: var(--secondary-background-color);
      color: var(--secondary-text-color);
      border-color: var(--secondary-border-color);
    }

    &:not(:disabled):hover,
    &:not(:disabled):focus {
      color: var(--secondary-hover-text-color);
      border-color: var(--secondary-hover-border-color);
      background-color: var(--secondary-hover-background-color);
    }
  }

  &.styled-tertiary {
    &,
    &:visited {
      background: var(--tertiary-background-color);
      color: var(--tertiary-text-color);
      border-color: var(--tertiary-border-color);
    }

    &:not(:disabled):hover,
    &:not(:disabled):focus {
      color: var(--tertiary-hover-text-color);
      border-color: var(--tertiary-hover-border-color);
      background-color: var(--tertiary-hover-background-color);
    }
  }
}
</style>